import { BlogTabs, type BlogTab } from '@/components/blog/BlogTabs';
import benchmark from '../../data/promptEngineeringBenchmark.json';

/**
 * LetterOutputTabs — blog12, interactive item 2: the actual letter each arm produced.
 *
 * The most honest figure in the post: the reader judges the writing directly
 * instead of trusting a score. Rendered as pre-wrap so the letter keeps the
 * line breaks it would have on paper. Score is shown per tab so the reader can
 * connect what they just read to what the judge gave it.
 *
 * Names are synthetic (Alex Kim / Sarah Chen from the benchmark fixture) — no
 * real person appears in these outputs.
 */

type Arm = {
  key: string;
  label: string;
  isRaisy: boolean;
  output: string;
  score: number;
};

/** Raisy's arm returns a JSON envelope; the letter is one field of it. */
const letterOf = (raw: string): string => {
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && typeof parsed.letter_text === 'string') {
      return parsed.letter_text;
    }
  } catch {
    /* plain text — the baseline arms */
  }
  return raw;
};

export const LetterOutputTabs = () => {
  const { arms, max_score: maxScore } = benchmark as unknown as {
    arms: Arm[];
    max_score: number;
  };

  const tabs: BlogTab[] = arms.map((a) => ({
    key: a.key,
    label: a.label,
    accent: a.isRaisy,
    content: (
      <>
        <div className="mb-2 flex items-baseline justify-between gap-3">
          <span className="text-label-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Letter as produced
          </span>
          <span className="text-label-xs text-slate-500 dark:text-slate-400">
            judge score{' '}
            <strong className={a.isRaisy ? 'text-cyan-700 dark:text-cyan-400' : 'text-slate-700 dark:text-slate-300'}>
              {a.score}
            </strong>{' '}
            / {maxScore}
          </span>
        </div>
        <pre className="max-h-96 sm:max-h-[28rem] overflow-auto rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/40 p-3 sm:p-4 text-[11.5px] sm:text-[12.5px] leading-relaxed text-slate-800 dark:text-slate-200 whitespace-pre-wrap break-words font-serif">
          {letterOf(a.output)}
        </pre>
      </>
    ),
  }));

  return (
    <BlogTabs
      tabs={tabs}
      caption="Unedited output from each arm, same task and context. Bracketed fields are the model's own placeholders. Names are synthetic benchmark fixtures."
    />
  );
};
